import React, { useEffect, useMemo, useState } from "react";
import api from "../api/client";
import { usePublishScreenData } from "../utils/aiScreenContext";
import { SoftwarePackage, SoftwareDeployment, Device } from "../types";
import { formatRelative } from "../utils/datetime";
import { useAuth } from "../context/AuthContext";
import { useCustomer } from "../context/CustomerContext";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import SoftwareCoverage from "../components/SoftwareCoverage";

export default function Software() {
  const { user } = useAuth();
  const { currentCustomerId } = useCustomer();
  const canManage = user?.role === "admin" || user?.role === "technician";
  const canDelete = user?.role === "admin";

  const [tab, setTab] = useState<"library" | "coverage" | "history">("library");
  const [packages, setPackages] = useState<SoftwarePackage[]>([]);
  const [deployments, setDeployments] = useState<SoftwareDeployment[]>([]);
  const [creating, setCreating] = useState<null | "winget" | "upload">(null);
  const [deploying, setDeploying] = useState<SoftwarePackage | null>(null);
  const [viewing, setViewing] = useState<SoftwareDeployment | null>(null);
  const libSort = useSort("name");

  // Publish the visible rows so the global AI Assist can answer
  // "what's on my screen". Tab-aware, because this screen shows two
  // different tables and publishing the wrong one is worse than
  // publishing none. Projected explicitly: machine output and
  // credential fields must not reach a prompt.
  usePublishScreenData(
    tab === "history" ? "Software deployment history" : "Software packages",
    useMemo(
      () =>
        tab === "history"
          ? deployments.map((d) => ({
              id: d.id,
              package: d.package_name,
              device: d.device_hostname,
              status: d.status,
              queued_at: d.queued_at,
              completed_at: d.completed_at ?? null,
              exit_code: d.exit_code ?? null,
              // NOT `output` - an installer's stdout is arbitrary text.
              error: d.error_message ?? null,
            }))
          : packages.map((p) => ({
              id: p.id,
              name: p.name,
              kind: p.kind,
              winget_id: p.winget_id ?? null,
              enabled: p.enabled,
            })),
      [tab, deployments, packages],
    ),
    `${tab} tab`,
  );

  const loadPackages = () => api.get("/api/v1/software/packages").then((r) => setPackages(r.data));
  const loadDeployments = () =>
    api.get("/api/v1/software/deployments?limit=200").then((r) => setDeployments(r.data));
  // Refetch when the global customer selector changes so the deployments
  // history reflects the selected customer (the GET interceptor scopes the call).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadPackages();
    loadDeployments();
  }, [currentCustomerId]);

  const deletePackage = async (p: SoftwarePackage) => {
    if (!window.confirm(`Delete package "${p.name}"? This cannot be undone.`)) return;
    await api.delete(`/api/v1/software/packages/${p.id}`);
    loadPackages();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">Software</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Push winget packages or uploaded MSI / EXE installers to devices. Every deployment is
            audit-logged.
          </p>
        </div>
        {canManage && tab === "library" && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCreating("winget")}
              className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700"
            >
              + Winget package
            </button>
            <button
              onClick={() => setCreating("upload")}
              className="text-sm bg-slate-700 text-white px-4 py-1.5 rounded-lg hover:bg-slate-800"
            >
              + Upload MSI / EXE
            </button>
          </div>
        )}
      </div>

      <div className="flex gap-1 mb-4 border-b dark:border-gray-700">
        {(["library", "coverage", "history"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px capitalize ${
              tab === t
                ? "border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400"
                : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            }`}
          >
            {t === "library"
              ? `Library (${packages.length})`
              : t === "coverage"
                ? "Coverage"
                : `Deployments (${deployments.length})`}
          </button>
        ))}
      </div>

      {tab === "library" && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
          {packages.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-10">
              No packages yet. Add a winget id or upload an MSI/EXE to get started.
            </p>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 uppercase">
                <tr>
                  <SortableTh label="Name" sortKey="name" sort={libSort.sort} onSort={libSort.toggle} />
                  <SortableTh label="Kind" sortKey="kind" sort={libSort.sort} onSort={libSort.toggle} hide="hidden md:table-cell" />
                  <th className="px-4 py-2 text-left hidden lg:table-cell">Source / Size</th>
                  <SortableTh label="Updated" sortKey="updated_at" sort={libSort.sort} onSort={libSort.toggle} hide="hidden lg:table-cell" />
                  <SortableTh label="Enabled" sortKey="enabled" sort={libSort.sort} onSort={libSort.toggle} hide="hidden sm:table-cell" />
                  {canManage && <th className="px-4 py-2 text-right">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {sortRows(packages, libSort.sort, (p, key) => {
                  switch (key) {
                    case "kind":
                      return (p.kind || "").toLowerCase();
                    case "updated_at":
                      return p.updated_at ? Date.parse(p.updated_at) : 0;
                    case "enabled":
                      return p.enabled ? 1 : 0;
                    default:
                      return (p.name || "").toLowerCase();
                  }
                }).map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                    <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-gray-100">{p.name}</td>
                    <td className="px-4 py-2.5 hidden md:table-cell">
                      <KindBadge kind={p.kind} />
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-600 dark:text-gray-400 hidden lg:table-cell">
                      {p.kind === "winget" ? (
                        <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">{p.winget_id}</code>
                      ) : p.file_size_bytes ? (
                        `${(p.file_size_bytes / (1024 * 1024)).toFixed(1)} MB`
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-500 dark:text-gray-400 hidden lg:table-cell">
                      {formatRelative(p.updated_at)}
                    </td>
                    <td className="px-4 py-2.5 text-xs hidden sm:table-cell">
                      {p.enabled ? (
                        <span className="text-green-700 dark:text-green-400">Enabled</span>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">Disabled</span>
                      )}
                    </td>
                    {canManage && (
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        <button
                          onClick={() => setDeploying(p)}
                          disabled={!p.enabled}
                          className="text-xs bg-blue-600 text-white px-2 py-1 rounded mr-1 hover:bg-blue-700 disabled:opacity-40"
                        >
                          Deploy
                        </button>
                        {canDelete && (
                          <button
                            onClick={() => deletePackage(p)}
                            className="text-xs border border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 px-2 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/30"
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "coverage" && (
        <SoftwareCoverage
          canManage={canManage}
          packages={packages}
          onDeploy={(pkg) => setDeploying(pkg)}
        />
      )}

      {tab === "history" && <DeployHistoryTable deployments={deployments} onView={setViewing} />}

      {creating === "winget" && (
        <NewWingetModal
          onClose={() => setCreating(null)}
          onCreated={() => {
            setCreating(null);
            loadPackages();
          }}
        />
      )}
      {creating === "upload" && (
        <UploadInstallerModal
          onClose={() => setCreating(null)}
          onCreated={() => {
            setCreating(null);
            loadPackages();
          }}
        />
      )}
      {deploying && (
        <DeployModal
          pkg={deploying}
          onClose={() => setDeploying(null)}
          onLaunched={() => {
            setDeploying(null);
            loadDeployments();
            setTab("history");
          }}
        />
      )}
      {viewing && <DeployDetailModal dep={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

function KindBadge({ kind }: { kind: string }) {
  const cls =
    kind === "winget"
      ? "bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
      : kind === "msi"
        ? "bg-purple-50 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300"
        : kind === "exe"
          ? "bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
          : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300";
  return <span className={`text-xs px-2 py-0.5 rounded-full ${cls}`}>{kind.toUpperCase()}</span>;
}

function NewWingetModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState("");
  const [wingetId, setWingetId] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/api/v1/software/packages", {
        name,
        description: desc,
        kind: "winget",
        winget_id: wingetId,
      });
      onCreated();
    } catch (err: any) {
      alert(err.response?.data?.detail || "Create failed");
      setBusy(false);
    }
  };
  return (
    <Modal title="New winget package" onClose={onClose}>
      <Field label="Display name">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full border rounded px-3 py-1.5 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
        />
      </Field>
      <Field label="Winget ID (--id)">
        <input
          value={wingetId}
          onChange={(e) => setWingetId(e.target.value)}
          placeholder="e.g., Google.Chrome, Adobe.Acrobat.Reader.64-bit"
          className="w-full border rounded px-3 py-1.5 text-sm font-mono dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
        />
      </Field>
      <Field label="Description (optional)">
        <input
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          className="w-full border rounded px-3 py-1.5 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
        />
      </Field>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        Tip: find package IDs at{" "}
        <a
          href="https://winget.run"
          target="_blank"
          rel="noreferrer"
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          winget.run
        </a>
        or run <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">winget search</code> on a test machine.
      </p>
      <ModalActions>
        <button onClick={onClose} className="text-sm border px-4 py-1.5 rounded">
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={!name || !wingetId || busy}
          className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {busy ? "Creating…" : "Create"}
        </button>
      </ModalActions>
    </Modal>
  );
}

function UploadInstallerModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [silentArgs, setSilentArgs] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const kind = file?.name.toLowerCase().endsWith(".msi") ? "msi" : "exe";
  const submit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", name);
      fd.append("kind", kind);
      if (desc) fd.append("description", desc);
      if (silentArgs) fd.append("silent_args", silentArgs);
      fd.append("file", file);
      await api.post("/api/v1/software/packages/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onCreated();
    } catch (err: any) {
      alert(err.response?.data?.detail || "Upload failed");
      setBusy(false);
    }
  };
  return (
    <Modal title="Upload MSI / EXE installer" onClose={onClose}>
      <Field label="Display name">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full border rounded px-3 py-1.5 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
        />
      </Field>
      <Field label="Installer file (.msi or .exe)">
        <input
          type="file"
          accept=".msi,.exe"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="text-sm w-full"
        />
        {file && (
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {file.name} · {(file.size / (1024 * 1024)).toFixed(2)} MB · detected kind: {kind}
          </div>
        )}
      </Field>
      <Field label="Silent install args (optional)">
        <input
          value={silentArgs}
          onChange={(e) => setSilentArgs(e.target.value)}
          placeholder={
            kind === "msi"
              ? "Extra MSI properties, e.g., REBOOT=ReallySuppress"
              : "EXE flags, e.g., /S or /SILENT or /VERYSILENT /SUPPRESSMSGBOXES"
          }
          className="w-full border rounded px-3 py-1.5 text-sm font-mono dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          MSI installs run with <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">/quiet /norestart</code>{" "}
          automatically; this field adds extra args.
        </p>
      </Field>
      <Field label="Description (optional)">
        <input
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          className="w-full border rounded px-3 py-1.5 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
        />
      </Field>
      <ModalActions>
        <button onClick={onClose} className="text-sm border px-4 py-1.5 rounded">
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={!name || !file || busy}
          className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {busy ? "Uploading…" : "Upload"}
        </button>
      </ModalActions>
    </Modal>
  );
}

function DeployModal({
  pkg,
  onClose,
  onLaunched,
}: {
  pkg: SoftwarePackage;
  onClose: () => void;
  onLaunched: () => void;
}) {
  const [devices, setDevices] = useState<Device[]>([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [launching, setLaunching] = useState(false);
  useEffect(() => {
    api.get("/api/v1/devices?status=online").then((r) => setDevices(r.data));
  }, []);
  const filtered = useMemo(() => {
    if (!search) return devices;
    const q = search.toLowerCase();
    return devices.filter(
      (d) =>
        d.hostname.toLowerCase().includes(q) || (d.customer_name || "").toLowerCase().includes(q),
    );
  }, [devices, search]);
  const toggle = (id: number) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };
  const launch = async () => {
    if (selected.size === 0) return;
    if (
      !window.confirm(
        `Deploy "${pkg.name}" to ${selected.size} device(s)?\n\n` +
          `This is audit-logged. Installations run in silent mode and may take several minutes per device.`,
      )
    )
      return;
    setLaunching(true);
    try {
      await api.post(`/api/v1/software/packages/${pkg.id}/deploy`, {
        device_ids: Array.from(selected),
      });
      onLaunched();
    } catch (err: any) {
      alert(err.response?.data?.detail || "Launch failed");
      setLaunching(false);
    }
  };
  return (
    <Modal title={`Deploy "${pkg.name}"`} onClose={onClose} wide>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        Pick online devices to install on. Installation is silent and may take 1–10 minutes per
        device.
      </p>
      <input
        type="text"
        placeholder="Filter by hostname or customer…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="border rounded px-3 py-2 text-sm mb-3 w-full dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
      />
      <div className="border dark:border-gray-700 rounded overflow-hidden mb-4 max-h-80 overflow-y-auto">
        <table className="min-w-full text-sm">
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {filtered.map((d) => (
              <tr
                key={d.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer"
                onClick={() => toggle(d.id)}
              >
                <td className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={selected.has(d.id)}
                    onChange={() => toggle(d.id)}
                  />
                </td>
                <td className="px-3 py-2 font-medium text-gray-800 dark:text-gray-200">{d.hostname}</td>
                <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{d.customer_name || "—"}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={3} className="px-3 py-8 text-center text-gray-400 dark:text-gray-500 text-sm">
                  No online devices match.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-600 dark:text-gray-400">{selected.size} selected</span>
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="text-sm border dark:border-gray-600 dark:text-gray-200 px-4 py-1.5 rounded">
            Cancel
          </button>
          <button
            onClick={launch}
            disabled={selected.size === 0 || launching}
            className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {launching ? "Launching…" : `Deploy to ${selected.size}`}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function DeployHistoryTable({
  deployments,
  onView,
}: {
  deployments: SoftwareDeployment[];
  onView: (d: SoftwareDeployment) => void;
}) {
  const histSort = useSort("package_name");
  const statusBadge = (s: string) => {
    const cls =
      s === "installed"
        ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
        : s === "failed" || s === "timed_out"
          ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
          : s === "running" || s === "sent"
            ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
            : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300";
    return <span className={`text-xs px-2 py-0.5 rounded-full ${cls}`}>{s}</span>;
  };
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 uppercase">
          <tr>
            <SortableTh label="Package" sortKey="package_name" sort={histSort.sort} onSort={histSort.toggle} />
            <SortableTh label="Device" sortKey="device" sort={histSort.sort} onSort={histSort.toggle} />
            <SortableTh label="Status" sortKey="status" sort={histSort.sort} onSort={histSort.toggle} />
            <SortableTh label="Exit" sortKey="exit_code" sort={histSort.sort} onSort={histSort.toggle} />
            <SortableTh label="Duration" sortKey="duration_ms" sort={histSort.sort} onSort={histSort.toggle} />
            <SortableTh label="When" sortKey="queued_at" sort={histSort.sort} onSort={histSort.toggle} />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
          {sortRows(deployments, histSort.sort, (r, key) => {
            switch (key) {
              case "device":
                return (r.device_hostname || `#${r.device_id}`).toLowerCase();
              case "status":
                return (r.status || "").toLowerCase();
              case "exit_code":
                return r.exit_code ?? 0;
              case "duration_ms":
                return r.duration_ms ?? 0;
              case "queued_at":
                return r.queued_at ? Date.parse(r.queued_at) : 0;
              default:
                return (r.package_name || "").toLowerCase();
            }
          }).map((r) => (
            <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer" onClick={() => onView(r)}>
              <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-gray-100">
                {r.package_name || "(deleted)"}
              </td>
              <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300">
                {r.device_hostname || `#${r.device_id}`}
              </td>
              <td className="px-4 py-2.5">{statusBadge(r.status)}</td>
              <td className="px-4 py-2.5 text-xs">{r.exit_code ?? "—"}</td>
              <td className="px-4 py-2.5 text-xs">
                {r.duration_ms ? `${(r.duration_ms / 1000).toFixed(1)}s` : "—"}
              </td>
              <td className="px-4 py-2.5 text-xs text-gray-500 dark:text-gray-400">
                {formatRelative(r.queued_at)}
              </td>
            </tr>
          ))}
          {deployments.length === 0 && (
            <tr>
              <td colSpan={6} className="px-4 py-10 text-center text-gray-400 dark:text-gray-500 text-sm">
                No deployments yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function DeployDetailModal({ dep, onClose }: { dep: SoftwareDeployment; onClose: () => void }) {
  return (
    <Modal
      title={`${dep.package_name || "(deleted package)"} · ${dep.device_hostname || `#${dep.device_id}`}`}
      onClose={onClose}
      wide
    >
      <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        Status: <span className="font-medium text-gray-800 dark:text-gray-200">{dep.status}</span> · Exit:{" "}
        <span className="font-mono">{dep.exit_code ?? "—"}</span> · Duration:{" "}
        {dep.duration_ms ? `${(dep.duration_ms / 1000).toFixed(2)}s` : "—"}
      </div>
      {dep.error_message && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded p-3 text-xs text-red-700 dark:text-red-300 mb-3 font-mono">
          {dep.error_message}
        </div>
      )}
      <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Output</div>
      <pre className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded p-3 text-xs font-mono whitespace-pre-wrap max-h-96 overflow-y-auto text-gray-800 dark:text-gray-200">
        {dep.output || "(empty)"}
      </pre>
    </Modal>
  );
}

function Modal({
  title,
  onClose,
  wide,
  children,
}: {
  title: string;
  onClose: () => void;
  wide?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        className={`bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full ${wide ? "max-w-2xl" : "max-w-lg"} p-6 max-h-[90vh] overflow-y-auto`}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold dark:text-gray-100">{title}</h2>
          <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-3">
      <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">{label}</label>
      {children}
    </div>
  );
}

function ModalActions({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center justify-end gap-3 mt-2">{children}</div>;
}
